// @ts-check
// File: app/src/utils/finishBy.js
//
// Finish-by deadline (issue #8). The user sets a clock time the session has to
// end by ("06:30"); during playback the coach compares its projected finish
// with that time and, when the session is heading over, proposes ONE trim that
// lands it on time:
//
//   1. shorten the rests still to come (by up to MAX_REST_CUT),
//   2. then take sets off the repeating blocks still to come, last block first,
//   3. and only then drop whole blocks still to come, the last one first.
//
// The cool-down gets no protection: an active cool-down barely helps
// (docs/training_science.md §4), so it is the first block to go.
//
// Savings are measured, not guessed: each candidate plan is recompiled with the
// engine's own options and its remaining time summed, so set-scheme ramps, rep
// estimates and the rest filter all count exactly as they will run.
//
// Pure: never mutates the plan, no React, no clock reads (callers pass `now`).

import { compileWorkoutPlan } from "./workoutCompiler";
import { isInjectedChangeover, isProtocolWindowGroup } from "./planVisuals";
import { isRepeatUnit, isTimedUnit } from "./planNodeUtils";
import { toSeconds } from "./time";

/** @typedef {import("../types/plan").PlanNode} PlanNode */
/** @typedef {import("../types/plan").QueueItem} QueueItem */

/** Heading over by less than this is noise, not a reason to interrupt. */
export const OVERRUN_MARGIN_SEC = 60;
/** After "Keep going", ask again only once it has got this much worse. */
export const REOFFER_STEP_SEC = 120;
/** Rests are never cut by more than this fraction… */
export const MAX_REST_CUT = 0.3;
/** …nor below this many seconds. */
export const MIN_REST_SEC = 10;
/** A deadline more than this far in the past is read as tomorrow's. */
const PAST_GRACE_MS = 60 * 60 * 1000;

// ---------------------------------------------------------------------------
// Deadline
// ---------------------------------------------------------------------------

/**
 * The next moment the clock reads `hhmm` ("06:30"), as epoch ms. A time up to
 * an hour ago counts as today's (already overdue) rather than tomorrow's: a
 * 06:30 deadline set at 06:35 means "I'm late", not "tomorrow morning".
 *
 * @param {string} hhmm
 * @param {number} now  epoch ms
 * @returns {number | null}
 */
export const resolveFinishBy = (hhmm, now) => {
  const m = /^(\d{1,2}):(\d{2})$/.exec(String(hhmm || "").trim());
  if (!m) return null;
  const h = Number(m[1]);
  const min = Number(m[2]);
  if (h > 23 || min > 59) return null;
  const at = new Date(now);
  at.setHours(h, min, 0, 0);
  if (at.getTime() < now - PAST_GRACE_MS) at.setDate(at.getDate() + 1);
  return at.getTime();
};

/** "06:30" for an epoch ms, in the user's locale. */
export const formatClock = (ms) =>
  new Date(ms).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });

/**
 * Seconds the session is projected to run past the deadline (negative when it
 * will finish early).
 *
 * @param {{ now: number, remainingSec: number, finishBy: number }} args
 */
export const projectedOverrunSec = ({ now, remainingSec, finishBy }) =>
  Math.round((now + Math.max(0, remainingSec || 0) * 1000 - finishBy) / 1000);

/**
 * Whether to put a trim suggestion in front of the user now.
 *
 * @param {object} args
 * @param {number} args.overSec          projected overrun
 * @param {boolean} args.atBreak         a rest, the Ready gate or paused:
 *                                       never interrupt a set
 * @param {number | null} args.dismissedAtSec  the overrun when the user last
 *                                       chose "Keep going", if they did
 */
export const shouldOfferTrim = ({ overSec, atBreak, dismissedAtSec }) => {
  if (!atBreak || !(overSec > OVERRUN_MARGIN_SEC)) return false;
  if (dismissedAtSec == null) return true;
  return overSec >= dismissedAtSec + REOFFER_STEP_SEC;
};

// ---------------------------------------------------------------------------
// Plan tree helpers
// ---------------------------------------------------------------------------

/** @param {PlanNode} plan */
const clone = (plan) => JSON.parse(JSON.stringify(plan));

/**
 * @param {PlanNode} root
 * @param {ReadonlyArray<string | number>} path
 * @returns {PlanNode | undefined}
 */
const nodeAt = (root, path) => {
  /** @type {any} */
  let node = root;
  for (const key of path) {
    if (node == null) return undefined;
    node = node[key];
  }
  return node;
};

const pathKey = (path) => path.join(".");

// Identity of one compiled phase that survives every edit this module makes:
// rests change value (not shape), set cuts drop trailing rounds, and a dropped
// block only shifts the indices of blocks after it, which are all still to come.
/** @param {QueueItem} item */
const itemKey = (item) => `${pathKey(item.sourceTreePath)}#${item.roundKey ?? ""}`;

/**
 * @param {QueueItem[]} queue
 * @param {number} currentIndex
 */
const doneKeysOf = (queue, currentIndex) =>
  new Set(queue.slice(0, currentIndex + 1).map(itemKey));

/**
 * Seconds still to run after the current phase, as compiled.
 *
 * @param {PlanNode} plan
 * @param {Set<string>} doneKeys
 * @param {object} compileOpts
 */
const futureSec = (plan, doneKeys, compileOpts) => {
  const { queue } = compileWorkoutPlan(plan, compileOpts);
  let total = 0;
  for (const item of queue) {
    if (!doneKeys.has(itemKey(item))) total += item.durationSec;
  }
  return total;
};

const isPrefix = (prefix, path) =>
  prefix.length <= path.length && prefix.every((k, i) => path[i] === k);

const round5 = (sec) => Math.round(sec / 5) * 5;

// ---------------------------------------------------------------------------
// Candidate edits
// ---------------------------------------------------------------------------

// Authored rests with at least one instance still to come. Injected changeovers
// are logistics (moving between stations), not recovery, and stay as they are.
const futureRestPaths = (future) => {
  const seen = new Map();
  for (const item of future) {
    const n = item.node;
    if (String(n?.type || "").toLowerCase() !== "rest") continue;
    if (isInjectedChangeover(n) || !isTimedUnit(n.unit)) continue;
    if (!(toSeconds(n.value, n.unit) > MIN_REST_SEC)) continue;
    seen.set(pathKey(item.sourceTreePath), item.sourceTreePath);
  }
  return [...seen.values()];
};

/** @param {PlanNode} plan */
const scaleRests = (plan, paths, factor) => {
  const next = clone(plan);
  for (const path of paths) {
    const n = nodeAt(next, path);
    const sec = toSeconds(n.value, n.unit);
    n.value = Math.max(MIN_REST_SEC, round5(sec * factor));
    n.unit = "s";
  }
  return next;
};

// Repeating blocks with a round still to come, last block first. Each carries
// the fewest rounds it may be cut to: never a round already started, never
// below half (a 4-set lift keeps 2), never below one. EMOM-style windows are
// left alone, since their round count is the length their label states.
const futureSetGroups = (plan, queue, currentIndex) => {
  const current = queue[currentIndex];
  const groups = new Map();
  for (const item of queue.slice(currentIndex + 1)) {
    const p = item.sourceTreePath;
    for (let len = p.length - 2; len >= 0; len -= 2) {
      const gPath = p.slice(0, len);
      const key = pathKey(gPath);
      if (groups.has(key)) continue;
      const g = nodeAt(plan, gPath);
      if (
        !g ||
        g.type !== "Group" ||
        !isRepeatUnit(g.unit) ||
        !(Number(g.value) > 1) ||
        isProtocolWindowGroup(g)
      ) {
        continue;
      }
      // The round in progress, when the current phase sits inside this group.
      let startedRounds = 0;
      if (current && isPrefix(gPath, current.sourceTreePath)) {
        const loopKey = g.id == null || g.id === "" ? `@${gPath.join(".")}` : g.id;
        startedRounds = (current.loopCounters?.[loopKey] ?? 0) + 1;
      }
      const value = Number(g.value);
      const min = Math.max(1, Math.ceil(value / 2), startedRounds);
      if (min < value) groups.set(key, { path: gPath, label: g.label, min });
    }
  }
  return [...groups.values()].sort((a, b) =>
    pathKey(b.path).localeCompare(pathKey(a.path), undefined, { numeric: true }),
  );
};

// Top-level blocks that have not started yet, last first.
const futureBlocks = (plan, queue, currentIndex) => {
  const current = queue[currentIndex];
  const currentRoot = current ? Number(current.sourceTreePath[1]) : -1;
  const out = [];
  (plan.children || []).forEach((child, i) => {
    if (i > currentRoot) out.push({ index: i, label: child.label });
  });
  return out.reverse();
};

// ---------------------------------------------------------------------------
// Proposal
// ---------------------------------------------------------------------------

/**
 * @typedef {(
 *   | { kind: "rests", pct: number }
 *   | { kind: "sets", label: string, from: number, to: number }
 *   | { kind: "block", label: string }
 * )} TrimChange
 */

/**
 * @typedef {object} TrimProposal
 * @property {PlanNode} plan         the trimmed plan, for applySessionEdit
 * @property {TrimChange[]} changes
 * @property {number} savedSec
 * @property {boolean} landsOnTime   false when even the deepest trim falls short
 */

/**
 * The smallest trim that takes `overSec` out of what is left of the session.
 *
 * @param {PlanNode} plan
 * @param {object} args
 * @param {QueueItem[]} args.queue      the engine's compiled queue
 * @param {number} args.currentIndex    the engine's position in it
 * @param {number} args.overSec         seconds to save
 * @param {object} [args.compileOpts]   the engine's compile options
 *                                      (skipLastRest, profile, historyMap)
 * @returns {TrimProposal | null}  null when nothing ahead can be trimmed
 */
export const proposeTrim = (
  plan,
  { queue, currentIndex, overSec, compileOpts = {} },
) => {
  if (!plan || !Array.isArray(queue) || !(overSec > 0)) return null;
  if (currentIndex < 0 || currentIndex >= queue.length) return null;

  const doneKeys = doneKeysOf(queue, currentIndex);
  const future = queue.filter((item) => !doneKeys.has(itemKey(item)));
  const baseline = futureSec(plan, doneKeys, compileOpts);
  const saved = (p) => baseline - futureSec(p, doneKeys, compileOpts);

  /** @type {TrimChange[]} */
  const changes = [];
  let working = plan;

  // 1. Rests: the gentlest cut that is enough, up to MAX_REST_CUT.
  const restPaths = futureRestPaths(future);
  if (restPaths.length > 0) {
    let chosen = null;
    for (let pct = 10; pct <= MAX_REST_CUT * 100; pct += 10) {
      const candidate = scaleRests(plan, restPaths, 1 - pct / 100);
      chosen = { plan: candidate, pct };
      if (saved(candidate) >= overSec) break;
    }
    if (chosen && saved(chosen.plan) > 0) {
      working = chosen.plan;
      changes.push({ kind: "rests", pct: chosen.pct });
      if (saved(working) >= overSec) {
        return { plan: working, changes, savedSec: saved(working), landsOnTime: true };
      }
    }
  }
  const afterRests = working;
  const restChanges = [...changes];

  // 2. Sets: one round at a time, round-robin from the last block back.
  const cutSets = (start) => {
    let p = start;
    const groups = futureSetGroups(p, queue, currentIndex);
    const cuts = new Map();
    let progress = true;
    while (progress && saved(p) < overSec) {
      progress = false;
      for (const g of groups) {
        const node = nodeAt(p, g.path);
        if (Number(node.value) <= g.min) continue;
        p = clone(p);
        const target = nodeAt(p, g.path);
        const from = cuts.get(pathKey(g.path))?.from ?? Number(target.value);
        target.value = Number(target.value) - 1;
        cuts.set(pathKey(g.path), { label: g.label, from, to: target.value });
        progress = true;
        if (saved(p) >= overSec) break;
      }
    }
    /** @type {TrimChange[]} */
    const setChanges = [...cuts.values()].map((c) => ({ kind: "sets", ...c }));
    return { plan: p, changes: setChanges };
  };

  const sets = cutSets(afterRests);
  if (saved(sets.plan) >= overSec) {
    return {
      plan: sets.plan,
      changes: [...restChanges, ...sets.changes],
      savedSec: saved(sets.plan),
      landsOnTime: true,
    };
  }

  // 3. Blocks: drop whole blocks still to come, last first, starting again from
  // the rest cut (no point trimming sets off a block that then goes).
  let dropped = afterRests;
  /** @type {TrimChange[]} */
  const blockChanges = [];
  for (const block of futureBlocks(afterRests, queue, currentIndex)) {
    if (saved(dropped) >= overSec) break;
    dropped = clone(dropped);
    dropped.children.splice(block.index, 1);
    blockChanges.push({ kind: "block", label: block.label || "a block" });
  }
  let finalPlan = dropped;
  let finalChanges = [...restChanges, ...blockChanges.reverse()];
  // Still short with every later block gone: cut sets in what is left.
  if (saved(finalPlan) < overSec) {
    const more = cutSets(finalPlan);
    finalPlan = more.plan;
    finalChanges = [...finalChanges, ...more.changes];
  }

  const savedSec = saved(finalPlan);
  if (finalChanges.length === 0 || savedSec <= 0) return null;
  return {
    plan: finalPlan,
    changes: finalChanges,
    savedSec,
    landsOnTime: savedSec >= overSec,
  };
};

// ---------------------------------------------------------------------------
// Wording
// ---------------------------------------------------------------------------

const listJoin = (items) =>
  items.length <= 1
    ? items.join("")
    : `${items.slice(0, -1).join(", ")} and ${items[items.length - 1]}`;

/**
 * One plain sentence for a proposal: "Shorten rests by 20% and take a set off
 * Bench Press".
 *
 * @param {TrimChange[]} changes
 */
export const describeTrim = (changes) => {
  const parts = [];
  const rests = changes.find((c) => c.kind === "rests");
  if (rests && rests.kind === "rests") parts.push(`shorten rests by ${rests.pct}%`);
  const sets = [];
  const blocks = [];
  for (const c of changes) {
    if (c.kind === "sets") sets.push(c);
    else if (c.kind === "block") blocks.push(c.label);
  }
  if (sets.length > 0) {
    const one = sets.every((c) => c.from - c.to === 1);
    const labels = sets.map((c) =>
      one ? c.label || "a block" : `${c.label || "a block"} (${c.from} → ${c.to} sets)`,
    );
    parts.push(`${one ? "take a set off" : "cut sets on"} ${listJoin(labels)}`);
  }
  if (blocks.length > 0) parts.push(`skip ${listJoin(blocks)}`);
  const sentence = listJoin(parts);
  return sentence.charAt(0).toUpperCase() + sentence.slice(1);
};
